import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, GraduationCap, Building2 } from "lucide-react";
import client, { errText, detectOrgSlug, fetchCobrand } from "../../lib/api";
import Stepper from "./Stepper";
import DirectoryCombobox from "../../components/DirectoryCombobox";

// Wave 3c (Shadab): the year picker is structured and adapts to the
// person's STREAM, not just medical. Every stream gets year options that
// fit how long its programme runs, and the medical and allied streams
// carry an internship year. Deterministic; an unrecognised programme
// falls to a safe three-to-four year set.
function yearOptionsFor(programme) {
  const p = ` ${(programme || "").toLowerCase()} `;
  const has = (...ks) => ks.some((k) => p.includes(k));
  const GRAD = "Graduated";
  // Medical and allied health: five years plus an internship year.
  if (has("mbbs", "bds", "bams", "bhms", "bums", "nursing", "b.pharm", "bpharm",
    "d.pharm", "pharm", "bvsc", "veterinary", "physio", "bpt", "dental",
    "medicine", "medical", "homeopath", "ayurved")) {
    return ["1st year", "2nd year", "3rd year", "4th year", "5th year",
      "Internship year", GRAD];
  }
  // Five-year integrated or professional: architecture, integrated law,
  // dual degrees.
  if (has("b.arch", "barch", "architecture", "integrated", " ba llb",
    " bba llb", "ballb", "b.a. ll", "dual degree", "5 year", "five year")) {
    return ["1st year", "2nd year", "3rd year", "4th year", "5th year", GRAD];
  }
  // Postgraduate: two years.
  if (has(" m.tech", " mtech", " m.sc", " msc", " m.com", " mcom", " m.a ",
    " ma ", " mba", " mca", " llm", " m.arch", " pgdm", "master",
    "post graduate", "postgraduate")) {
    return ["1st year", "Final year", GRAD];
  }
  // Everything else: science, commerce, arts, technology and engineering,
  // three-year law, and the rest. Three to four years, covered by ordinals
  // plus a Final year so a four-year student still fits.
  return ["1st year", "2nd year", "3rd year", "Final year", GRAD];
}

const INTERNSHIP_OPTIONS = [
  { value: "", label: "Select..." },
  { value: "yes", label: "Yes, doing one now" },
  { value: "lined_up", label: "Lined up / starting soon" },
  { value: "no", label: "Not yet" },
  { value: "na", label: "Not applicable" },
];

/**
 * A6: Education intake. Two rows by default. When a co-brand slug is
 * present in the URL (klareet.com/c/<slug> or ?partner=<slug>) the
 * institution field autofills with the co-brand display name.
 */
export default function EducationIntakeScreen({ product }) {
  const nav = useNavigate();
  const base = product === "grow" ? "/grow" : "";
  const [rows, setRows] = useState([
    { institution: "", programme: "", year: "", status: "current" },
  ]);
  const [orgSlug, setOrgSlug] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [gender, setGender] = useState("");
  const [internship, setInternship] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      const slug = detectOrgSlug();
      if (slug) {
        setOrgSlug(slug);
        const cb = await fetchCobrand(slug);
        if (cb?.display_name) {
          setOrgName(cb.display_name);
          setRows((r) => {
            const copy = [...r];
            if (!copy[0].institution) copy[0] = { ...copy[0], institution: cb.display_name };
            return copy;
          });
        }
      }
      try {
        const { data } = await client.get("/onboarding");
        if (Array.isArray(data.education) && data.education.length) {
          setRows(data.education.map((r) => ({ ...r })));
        }
        if (data.gender) setGender(data.gender);
        if (data.internship) setInternship(data.internship);
      } catch (_e) { /* first time */ }
    })();
  }, []);

  const update = (i, key, value) => {
    setRows((r) => r.map((row, ix) => (ix === i ? { ...row, [key]: value } : row)));
  };

  const addRow = () => setRows((r) => [...r, { institution: "", programme: "", year: "", status: "current" }]);
  const removeRow = (i) => setRows((r) => r.length > 1 ? r.filter((_, ix) => ix !== i) : r);

  const canContinue = rows.some((r) => r.institution.trim() && r.programme.trim());

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const cleaned = rows
        .map((r) => ({
          institution: r.institution.trim(),
          programme: r.programme.trim(),
          year: (r.year || "").trim() || null,
          status: r.status || "current",
        }))
        .filter((r) => r.institution && r.programme);
      await client.put("/onboarding", {
        org_slug: orgSlug,
        education: cleaned,
        experience_chips: [],
        goal: "",
        posture: "",
        gender: gender || null,
        internship: internship || null,
        step_index: 2,
        is_complete: false,
      });
      nav(`${base}/onboarding/experience`);
    } catch (e) {
      setErr(errText(e, "That did not save. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-shell relative overflow-hidden">
      <div className="k-orb k-orb-blue"  style={{ top: "-6rem", right: "-6rem", width: "20rem", height: "20rem" }} />
      <div className="k-orb k-orb-amber" style={{ bottom: "-6rem", left: "20%",  width: "18rem", height: "18rem" }} />
      <div className="relative max-w-2xl mx-auto px-5 pt-10 pb-24">
        <Stepper current={1} total={3} />
        <div className="mt-6 k-fade-in">
          <div className="k-chip k-chip-teal mb-3"><GraduationCap size={12} /> education</div>
          <h1 className="klareet-serif mb-3" style={{ fontSize: "clamp(1.8rem, 4vw, 2.5rem)", lineHeight: 1.1 }} data-testid="education-title">
            Where are you learning, or where did you learn?
          </h1>
          <p className="k-quiet mb-6">Just enough so I can pitch my questions at the right level. Add a second row if you want.</p>
        </div>

        {orgName && (
          <div
            className="k-card mb-5 flex items-center gap-3 k-fade-in"
            style={{ animationDelay: "60ms", background: "hsl(var(--klareet-blue-soft))" }}
            data-testid="cobrand-autofill"
          >
            <Building2 size={16} style={{ color: "hsl(var(--klareet-blue-strong))" }} />
            <div className="text-sm">
              <span style={{ color: "hsl(var(--klareet-ink))" }}>Coming in through </span>
              <span className="font-semibold" style={{ color: "hsl(var(--klareet-blue-strong))" }}>{orgName}</span>
              <span className="k-quiet"> · we pre-filled the first row</span>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {rows.map((row, i) => (
            <div key={i} className="k-card k-fade-in" style={{ animationDelay: `${80 + i * 60}ms` }} data-testid={`edu-row-${i}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="k-label">Institution</label>
                  <div className="mt-1">
                    <DirectoryCombobox
                      kind="institution"
                      value={row.institution}
                      placeholder="college, university, or school"
                      testid={`edu-institution-${i}`}
                      onChange={(v) => update(i, "institution", v)}
                    />
                  </div>
                </div>
                <div>
                  <label className="k-label">Programme</label>
                  <div className="mt-1">
                    <DirectoryCombobox
                      kind="programme"
                      value={row.programme}
                      placeholder="what you study"
                      testid={`edu-programme-${i}`}
                      onChange={(v) => update(i, "programme", v)}
                    />
                  </div>
                </div>
                <div>
                  <label className="k-label">Year</label>
                  <select className="k-input mt-1" value={row.year || ""} onChange={(e) => update(i, "year", e.target.value)} data-testid={`edu-year-${i}`}>
                    <option value="">Select...</option>
                    {yearOptionsFor(row.programme).map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="k-label">Status</label>
                  <select className="k-input mt-1" value={row.status} onChange={(e) => update(i, "status", e.target.value)} data-testid={`edu-status-${i}`}>
                    <option value="current">still there</option>
                    <option value="graduated">done</option>
                    <option value="paused">on pause</option>
                  </select>
                </div>
              </div>
              {rows.length > 1 && (
                <button onClick={() => removeRow(i)} className="k-btn k-btn-ghost !py-1.5 !px-3 mt-3 text-xs" data-testid={`edu-remove-${i}`}>
                  Remove this row
                </button>
              )}
            </div>
          ))}
          <button onClick={addRow} className="k-btn k-btn-ghost text-sm" data-testid="edu-add">
            Add another
          </button>
        </div>

        {/* Wave 3c (Shadab): internship maps the assessment. An interning
            student meets some workplace scenarios alongside the student
            ones, so the questions match their real week. */}
        <div className="mt-6">
          <label className="k-label block mb-1" htmlFor="edu-internship">
            Are you doing an internship right now?
          </label>
          <select
            id="edu-internship"
            className="k-input"
            value={internship}
            onChange={(e) => setInternship(e.target.value)}
            data-testid="edu-internship"
          >
            {INTERNSHIP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Optional, self declared. Never used to narrow what Klair
            shows you. It only helps your college see the shape of the
            whole group, never you. */}
        <div className="mt-6">
          <label className="k-quiet !text-xs block mb-1" htmlFor="edu-gender">
            Gender (optional, and only you and aggregate stats ever use this)
          </label>
          <select
            id="edu-gender"
            className="k-input"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            data-testid="edu-gender"
          >
            <option value="">Select...</option>
            <option value="woman">Woman</option>
            <option value="man">Man</option>
            <option value="non-binary">Non-binary</option>
            <option value="prefer not to say">Prefer not to say</option>
          </select>
        </div>

        {err && <div className="mt-4 k-quiet" style={{ color: "hsl(var(--destructive))" }}>{err}</div>}

        <div className="mt-8 flex justify-between items-center">
          <button onClick={() => nav(`${base}/welcome`)} className="k-btn k-btn-ghost" data-testid="edu-back">
            Back
          </button>
          <button onClick={submit} disabled={!canContinue || busy} className="k-btn k-btn-accent" data-testid="edu-continue">
            {busy ? "Saving." : "Continue"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
